import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  FileButton,
  Grid,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconBolt,
  IconCirclePlus,
  IconLanguage,
  IconRocket,
  IconSparkles,
  IconTrash,
  IconUpload,
  IconWand,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { useEffect, useMemo, useState } from 'react';
import { trpc } from '../../lib/trpc';
import { TEMPLATE_LANGUAGES } from '../../../../api/src/modules/whatsapp/template-languages';
import { validateTemplate } from '../../../../api/src/modules/whatsapp/template-validator';
import type {
  CreateTemplateInput,
  TemplateButton,
} from '../../../../api/src/modules/whatsapp/schema';
import { TemplatePreview } from '../../components/TemplatePreview';
import {
  CarouselCardsEditor,
  emptyCardDraft,
  type CardDraft,
  type CardLayout,
} from '../../components/templates/CarouselCardsEditor';

type Category = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
type ParamFormat = 'POSITIONAL' | 'NAMED';
type HeaderFormat = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'GIF' | 'DOCUMENT' | 'LOCATION';
type TemplateType = 'STANDARD' | 'CAROUSEL' | 'LIMITED_TIME_OFFER';

const CATEGORIES: Category[] = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const HEADER_FORMATS: HeaderFormat[] = [
  'NONE',
  'TEXT',
  'IMAGE',
  'VIDEO',
  'GIF',
  'DOCUMENT',
  'LOCATION',
];
/** LTO templates only allow image/video headers (or none). */
const LTO_HEADER_FORMATS: HeaderFormat[] = ['NONE', 'IMAGE', 'VIDEO'];
const BUTTON_TYPES = [
  'QUICK_REPLY',
  'URL',
  'PHONE_NUMBER',
  'COPY_CODE',
  'FLOW',
  'CATALOG',
  'MPM',
  'VOICE_CALL',
] as const;

function NewTemplate() {
  const navigate = useNavigate();

  // ── Form state ─────────────────────────────────────────────────
  const [templateName, setTemplateName] = useState('');
  const [language, setLanguage] = useState('en_US');
  const [category, setCategory] = useState<Category>('MARKETING');
  const [allowCategoryChange, setAllowCategoryChange] = useState(true);
  const [parameterFormat, setParameterFormat] = useState<ParamFormat>('POSITIONAL');

  const [headerFormat, setHeaderFormat] = useState<HeaderFormat>('NONE');
  const [headerText, setHeaderText] = useState('');
  const [headerExample, setHeaderExample] = useState('');
  const [headerHandleId, setHeaderHandleId] = useState<string | null>(null);
  const [headerMediaPreview, setHeaderMediaPreview] = useState<string | null>(null);

  const [bodyText, setBodyText] = useState('');
  const [bodyExamples, setBodyExamples] = useState<string[]>([]);
  const [bodyVariableNames, setBodyVariableNames] = useState<string[]>([]);

  const [footerText, setFooterText] = useState('');

  const [buttons, setButtons] = useState<TemplateButton[]>([]);

  // Template type — carousel and LTO are structural variants of MARKETING/
  // UTILITY templates. Switching type clears the other variant's state.
  const [templateType, setTemplateType] = useState<TemplateType>('STANDARD');
  const defaultCardLayout: CardLayout = { headerFormat: 'IMAGE', hasBody: true, buttonTypes: [] };
  const [cardLayout, setCardLayout] = useState<CardLayout>(defaultCardLayout);
  const [carouselCards, setCarouselCards] = useState<CardDraft[]>([
    emptyCardDraft(defaultCardLayout),
    emptyCardDraft(defaultCardLayout),
  ]);
  const [ltoText, setLtoText] = useState('');
  const [ltoHasExpiration, setLtoHasExpiration] = useState(true);

  // Optional validity period (message_send_ttl_seconds).
  const [ttlSeconds, setTtlSeconds] = useState<number | ''>('');

  // AUTHENTICATION-only
  const [addSecurityRecommendation, setAddSecurityRecommendation] = useState(true);
  const [codeExpirationMinutes, setCodeExpirationMinutes] = useState<number | ''>(10);
  const [otpButtonText, setOtpButtonText] = useState('Copy code');

  // ── Variable detection ─────────────────────────────────────────
  const detectedPositional = useMemo(() => {
    const out = new Set<number>();
    for (const m of bodyText.matchAll(/\{\{(\d+)\}\}/g)) out.add(Number(m[1]));
    return [...out].sort((a, b) => a - b);
  }, [bodyText]);
  const detectedNamed = useMemo(() => {
    const out = new Set<string>();
    for (const m of bodyText.matchAll(/\{\{([a-z][a-z0-9_]*)\}\}/g)) out.add(m[1]!);
    return [...out];
  }, [bodyText]);
  const expectedVarCount =
    parameterFormat === 'NAMED' ? detectedNamed.length : detectedPositional.length;

  // Keep examples array length in sync.
  useEffect(() => {
    setBodyExamples((prev) => {
      const next = prev.slice(0, expectedVarCount);
      while (next.length < expectedVarCount) next.push('');
      return next;
    });
    if (parameterFormat === 'NAMED') {
      setBodyVariableNames(detectedNamed);
    } else {
      setBodyVariableNames([]);
    }
  }, [expectedVarCount, parameterFormat, detectedNamed]);

  // Template type is a structural variant of MARKETING/UTILITY — reset it
  // when the category stops supporting it.
  useEffect(() => {
    if (category === 'AUTHENTICATION' && templateType !== 'STANDARD') {
      setTemplateType('STANDARD');
    } else if (category === 'UTILITY' && templateType === 'LIMITED_TIME_OFFER') {
      setTemplateType('STANDARD');
    }
  }, [category, templateType]);

  const changeTemplateType = (next: TemplateType) => {
    setTemplateType(next);
    if (next === 'CAROUSEL') {
      // Carousels have no top-level header/footer/buttons; card bodies are
      // positional-only.
      setHeaderFormat('NONE');
      setHeaderHandleId(null);
      setHeaderMediaPreview(null);
      setFooterText('');
      setButtons([]);
      setParameterFormat('POSITIONAL');
      setLtoText('');
    } else if (next === 'LIMITED_TIME_OFFER') {
      // LTO: no footer; header restricted to image/video; buttons restricted
      // to copy-code + URL (drop anything else).
      setFooterText('');
      if (!LTO_HEADER_FORMATS.includes(headerFormat)) {
        setHeaderFormat('NONE');
        setHeaderHandleId(null);
        setHeaderMediaPreview(null);
      }
      setButtons((prev) => prev.filter((b) => b.type === 'COPY_CODE' || b.type === 'URL'));
    } else {
      setLtoText('');
    }
  };

  // ── tRPC ────────────────────────────────────────────────────────
  const upload = trpc.whatsapp.templates.uploadHeaderMedia.useMutation();
  const create = trpc.whatsapp.templates.create.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Submitted to Meta for review', color: 'green' });
      navigate({ to: '/vendor/templates' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const aiEnabled = trpc.bots.ai.isEnabled.useQuery();
  const aiGenerate = trpc.bots.ai.generateTemplate.useMutation({
    onSuccess: (r) => {
      // Hydrate fields the AI returned.
      if (r.templateName) setTemplateName(canonicaliseName(r.templateName));
      if (r.bodyText) setBodyText(r.bodyText);
      if (r.footerText) setFooterText(r.footerText);
      if (r.category) setCategory(r.category as Category);
      notifications.show({ message: 'Draft generated — review before submit', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const aiTranslate = trpc.bots.ai.translateTemplate.useMutation({
    onSuccess: (r) => {
      if (r.bodyText) setBodyText(r.bodyText);
      if (r.footerText !== undefined && r.footerText !== null) setFooterText(r.footerText);
      notifications.show({ message: 'Translated', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  // ── Build the DTO Meta will see (also used for client-side validation) ──
  const dto: CreateTemplateInput = useMemo(
    () => ({
      templateName,
      language,
      category,
      allowCategoryChange,
      parameterFormat,
      bodyExamples: bodyExamples.length > 0 ? bodyExamples : undefined,
      bodyVariableNames: bodyVariableNames.length > 0 ? bodyVariableNames : undefined,
      header:
        headerFormat === 'NONE' || templateType === 'CAROUSEL'
          ? undefined
          : {
              format: headerFormat,
              text: headerFormat === 'TEXT' ? headerText : undefined,
              handleId: headerHandleId ?? undefined,
              example: headerFormat === 'TEXT' && headerExample ? headerExample : undefined,
            },
      body: { text: category === 'AUTHENTICATION' ? '' : bodyText },
      footer:
        category === 'AUTHENTICATION' || templateType !== 'STANDARD'
          ? undefined
          : footerText
            ? { text: footerText }
            : undefined,
      buttons: category === 'AUTHENTICATION' || templateType === 'CAROUSEL' ? undefined : buttons,
      carousel:
        templateType === 'CAROUSEL'
          ? {
              cards: carouselCards.map((c) => ({
                header: { format: cardLayout.headerFormat, handleId: c.handleId ?? '' },
                body: cardLayout.hasBody
                  ? {
                      text: c.bodyText,
                      examples: c.bodyExamples.length > 0 ? c.bodyExamples : undefined,
                    }
                  : undefined,
                buttons: c.buttons.length > 0 ? c.buttons : undefined,
              })),
            }
          : undefined,
      limitedTimeOffer:
        templateType === 'LIMITED_TIME_OFFER'
          ? { text: ltoText, hasExpiration: ltoHasExpiration }
          : undefined,
      // MARKETING TTL isn't reachable via the Cloud API this platform uses
      // (Meta requires the separate MM Lite API) — never forward it, even if
      // a value was set before switching category.
      messageSendTtlSeconds:
        category === 'MARKETING'
          ? undefined
          : typeof ttlSeconds === 'number'
            ? ttlSeconds
            : undefined,
      addSecurityRecommendation:
        category === 'AUTHENTICATION' ? addSecurityRecommendation : undefined,
      codeExpirationMinutes:
        category === 'AUTHENTICATION'
          ? typeof codeExpirationMinutes === 'number'
            ? codeExpirationMinutes
            : undefined
          : undefined,
      otpButtonType: 'COPY_CODE',
      otpButtonText: category === 'AUTHENTICATION' ? otpButtonText : undefined,
    }),
    [
      templateName,
      language,
      category,
      allowCategoryChange,
      parameterFormat,
      bodyExamples,
      bodyVariableNames,
      headerFormat,
      headerText,
      headerExample,
      headerHandleId,
      bodyText,
      footerText,
      buttons,
      templateType,
      cardLayout,
      carouselCards,
      ltoText,
      ltoHasExpiration,
      ttlSeconds,
      addSecurityRecommendation,
      codeExpirationMinutes,
      otpButtonText,
    ],
  );

  const validationError = useMemo(() => validateTemplate(dto), [dto]);

  // ── Handlers ────────────────────────────────────────────────────
  const handleHeaderFile = async (file: File | null) => {
    if (!file) return;
    const buf = await file.arrayBuffer();
    const base64 = bufferToBase64(buf);
    upload.mutate(
      { base64, mimeType: file.type, filename: file.name },
      {
        onSuccess: (r) => {
          setHeaderHandleId(r.headerHandle);
          setHeaderMediaPreview((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(file);
          });
          notifications.show({ message: 'Media uploaded', color: 'green' });
        },
        onError: (err) => notifications.show({ message: err.message, color: 'red' }),
      },
    );
  };

  /**
   * Card media upload — reuses the same header-media resumable upload. Returns
   * null (after surfacing an error toast) when the upload fails, so the caller
   * never leaves a swallowed rejection or a card stuck without a handle.
   */
  const uploadCardMedia = async (
    file: File,
  ): Promise<{ handleId: string; previewUrl: string } | null> => {
    try {
      const buf = await file.arrayBuffer();
      const base64 = bufferToBase64(buf);
      const r = await upload.mutateAsync({ base64, mimeType: file.type, filename: file.name });
      notifications.show({ message: 'Media uploaded', color: 'green' });
      return { handleId: r.headerHandle, previewUrl: URL.createObjectURL(file) };
    } catch (err) {
      notifications.show({
        message: err instanceof Error ? err.message : 'Media upload failed',
        color: 'red',
      });
      return null;
    }
  };

  const addButton = (type: (typeof BUTTON_TYPES)[number]) => {
    const next: TemplateButton =
      type === 'QUICK_REPLY'
        ? { type, text: 'Reply' }
        : type === 'URL'
          ? { type, text: 'Visit', url: 'https://example.com' }
          : type === 'PHONE_NUMBER'
            ? { type, text: 'Call us', phone_number: '+10000000000' }
            : type === 'COPY_CODE'
              ? { type, copyCodeExample: 'SAVE10' }
              : type === 'FLOW'
                ? {
                    type,
                    text: 'Open',
                    flowId: '',
                    flowAction: 'navigate',
                  }
                : type === 'CATALOG'
                  ? { type, text: 'View catalog' }
                  : type === 'MPM'
                    ? { type }
                    : { type, text: 'Voice call' };
    setButtons((prev) => [...prev, next]);
  };

  const updateButton = (i: number, patch: Partial<TemplateButton>) => {
    setButtons((prev) =>
      prev.map((b, idx) => (idx === i ? ({ ...b, ...patch } as TemplateButton) : b)),
    );
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group>
          <Button
            variant="subtle"
            component={Link}
            to="/vendor/templates"
            leftSection={<IconArrowLeft size={14} />}
          >
            Templates
          </Button>
        </Group>
        <Title order={2}>New template</Title>

        {aiEnabled.data && (
          <AiPanel
            generating={aiGenerate.isPending}
            translating={aiTranslate.isPending}
            currentBody={bodyText}
            currentFooter={footerText}
            onGenerate={(prompt) => aiGenerate.mutate({ prompt })}
            onTranslate={(targetLanguage) =>
              aiTranslate.mutate({ bodyText, footerText, targetLanguage })
            }
          />
        )}

        <Grid gap="md">
          {/* ── Builder pane ───────────────────────────── */}
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Card>
              <Stack gap="sm">
                <Group grow>
                  <TextInput
                    label="Template name"
                    description="Lowercase letters, digits, underscores"
                    placeholder="welcome_back"
                    value={templateName}
                    onChange={(e) => setTemplateName(canonicaliseName(e.currentTarget.value))}
                  />
                  <Select
                    label="Language"
                    searchable
                    data={TEMPLATE_LANGUAGES.map((l) => ({
                      value: l.code,
                      label: `${l.code} — ${l.name}`,
                    }))}
                    value={language}
                    onChange={(v) => setLanguage(v ?? 'en_US')}
                  />
                </Group>
                <Group grow>
                  <Select
                    label="Category"
                    data={CATEGORIES}
                    value={category}
                    onChange={(v) => setCategory((v as Category) ?? 'MARKETING')}
                  />
                  <Switch
                    label="Allow Meta to recategorise"
                    description="Recommended — Meta may flag mis-categorisation."
                    checked={allowCategoryChange}
                    onChange={(e) => setAllowCategoryChange(e.currentTarget.checked)}
                    mt={24}
                  />
                </Group>

                {category !== 'AUTHENTICATION' && (
                  <>
                    <SegmentedControl
                      value={templateType}
                      onChange={(v) => changeTemplateType(v as TemplateType)}
                      data={[
                        { value: 'STANDARD', label: 'Standard' },
                        { value: 'CAROUSEL', label: 'Carousel' },
                        ...(category === 'MARKETING'
                          ? [{ value: 'LIMITED_TIME_OFFER', label: 'Limited-time offer' }]
                          : []),
                      ]}
                    />

                    {templateType === 'LIMITED_TIME_OFFER' && (
                      <Alert color="orange" icon={<IconBolt size={14} />}>
                        Limited-time offer rules: MARKETING category only, no footer, image/video
                        header only, and buttons limited to Copy code + URL (copy code first).
                      </Alert>
                    )}

                    {templateType !== 'CAROUSEL' && (
                      <>
                        <Divider label="Header (optional)" labelPosition="left" />
                        <Group grow>
                          <Select
                            label="Format"
                            data={
                              templateType === 'LIMITED_TIME_OFFER'
                                ? LTO_HEADER_FORMATS
                                : HEADER_FORMATS
                            }
                            value={headerFormat}
                            onChange={(v) => {
                              setHeaderFormat((v as HeaderFormat) ?? 'NONE');
                              setHeaderHandleId(null);
                              setHeaderMediaPreview(null);
                            }}
                          />
                          {headerFormat === 'TEXT' && (
                            <TextInput
                              label="Header text"
                              description="Max 60 chars, at most one variable"
                              maxLength={60}
                              value={headerText}
                              onChange={(e) => setHeaderText(e.currentTarget.value)}
                            />
                          )}
                        </Group>
                        {headerFormat === 'GIF' && (
                          <Text size="xs" c="dimmed">
                            GIF headers require enrollment in Meta's Marketing Messages API — sends
                            via the standard Cloud API will fail. Upload an mp4 (max 3.5MB).
                          </Text>
                        )}
                        {headerFormat === 'TEXT' &&
                          /\{\{(?:\d+|[a-z][a-z0-9_]*)\}\}/.test(headerText) && (
                            <TextInput
                              label="Sample value for header variable"
                              value={headerExample}
                              onChange={(e) => setHeaderExample(e.currentTarget.value)}
                            />
                          )}
                        {(headerFormat === 'IMAGE' ||
                          headerFormat === 'VIDEO' ||
                          headerFormat === 'GIF' ||
                          headerFormat === 'DOCUMENT') && (
                          <Stack gap={4}>
                            <Group>
                              <FileButton
                                onChange={handleHeaderFile}
                                accept={
                                  headerFormat === 'IMAGE'
                                    ? 'image/*'
                                    : headerFormat === 'VIDEO' || headerFormat === 'GIF'
                                      ? 'video/mp4'
                                      : 'application/pdf'
                                }
                              >
                                {(props) => (
                                  <Button
                                    {...props}
                                    leftSection={<IconUpload size={14} />}
                                    loading={upload.isPending}
                                    variant="light"
                                  >
                                    Upload sample {headerFormat.toLowerCase()}
                                  </Button>
                                )}
                              </FileButton>
                              {headerHandleId && (
                                <Badge color="green" variant="light">
                                  Uploaded ✓
                                </Badge>
                              )}
                            </Group>
                            <Text size="xs" c="dimmed">
                              Meta requires a sample {headerFormat.toLowerCase()} during review. The
                              handle is reused on every send.
                            </Text>
                          </Stack>
                        )}
                      </>
                    )}

                    <Divider label="Body" labelPosition="left" />
                    <Textarea
                      label="Body text"
                      description="Use {{1}}, {{2}} (or {{name}} when format=NAMED). Max 1024 chars."
                      minRows={5}
                      autosize
                      maxLength={1024}
                      value={bodyText}
                      onChange={(e) => setBodyText(e.currentTarget.value)}
                    />
                    {(detectedPositional.length > 0 || detectedNamed.length > 0) && (
                      <Group>
                        <Select
                          label="Parameter format"
                          data={['POSITIONAL', 'NAMED']}
                          value={parameterFormat}
                          onChange={(v) => setParameterFormat((v as ParamFormat) ?? 'POSITIONAL')}
                        />
                      </Group>
                    )}
                    {expectedVarCount > 0 && (
                      <Stack gap={4}>
                        <Text size="sm" fw={500}>
                          Sample values
                        </Text>
                        {Array.from({ length: expectedVarCount }).map((_, i) => (
                          <TextInput
                            key={i}
                            label={
                              parameterFormat === 'NAMED'
                                ? `{{${bodyVariableNames[i] ?? `var_${i + 1}`}}}`
                                : `{{${detectedPositional[i] ?? i + 1}}}`
                            }
                            placeholder={`Example value ${i + 1}`}
                            value={bodyExamples[i] ?? ''}
                            onChange={(e) => {
                              // Read synchronously — React nulls e.currentTarget
                              // after the handler returns, and the setState
                              // updater below runs later (→ null.value crash).
                              const value = e.currentTarget.value;
                              setBodyExamples((prev) => {
                                const next = [...prev];
                                next[i] = value;
                                return next;
                              });
                            }}
                          />
                        ))}
                      </Stack>
                    )}

                    {templateType === 'LIMITED_TIME_OFFER' && (
                      <>
                        <Divider label="Limited-time offer" labelPosition="left" />
                        <TextInput
                          label="Offer text"
                          description={`${ltoText.length}/16 — plain text, e.g. “Expiring offer!”`}
                          maxLength={16}
                          value={ltoText}
                          onChange={(e) => setLtoText(e.currentTarget.value)}
                        />
                        <Switch
                          label="Show expiration countdown"
                          description="Renders “Offer ends in …” — the actual expiry time is provided per send."
                          checked={ltoHasExpiration}
                          onChange={(e) => setLtoHasExpiration(e.currentTarget.checked)}
                        />
                      </>
                    )}

                    {templateType === 'CAROUSEL' && (
                      <>
                        <Divider label="Carousel cards (2–10)" labelPosition="left" />
                        <CarouselCardsEditor
                          layout={cardLayout}
                          onLayoutChange={setCardLayout}
                          cards={carouselCards}
                          onCardsChange={setCarouselCards}
                          onUploadMedia={uploadCardMedia}
                        />
                      </>
                    )}

                    {templateType === 'STANDARD' && (
                      <>
                        <Divider label="Footer (optional)" labelPosition="left" />
                        <TextInput
                          label="Footer text"
                          description={`${footerText.length}/60 — no variables allowed`}
                          maxLength={60}
                          value={footerText}
                          onChange={(e) => setFooterText(e.currentTarget.value)}
                        />
                      </>
                    )}

                    {templateType !== 'CAROUSEL' && (
                      <>
                        <Divider label="Buttons (optional)" labelPosition="left" />
                        <ButtonsBuilder
                          buttons={buttons}
                          allowedTypes={
                            templateType === 'LIMITED_TIME_OFFER' ? ['COPY_CODE', 'URL'] : undefined
                          }
                          onAdd={addButton}
                          onUpdate={updateButton}
                          onRemove={(i) => setButtons((prev) => prev.filter((_, idx) => idx !== i))}
                        />
                      </>
                    )}

                    <Divider label="Delivery (optional)" labelPosition="left" />
                    {category === 'MARKETING' ? (
                      <Text size="xs" c="dimmed">
                        Custom validity period isn't available for Marketing templates here — Meta
                        only supports it via the separate Marketing Messages Lite API, which isn't
                        integrated on this platform. Marketing templates use Meta's default (30
                        days).
                      </Text>
                    ) : (
                      <NumberInput
                        label="Validity period (seconds)"
                        description="30–43200 (30s–12h). Leave blank for Meta default (30 days)."
                        min={30}
                        max={43200}
                        value={ttlSeconds}
                        onChange={(v) => setTtlSeconds(typeof v === 'number' ? v : '')}
                      />
                    )}
                  </>
                )}

                {category === 'AUTHENTICATION' && (
                  <>
                    <Alert color="blue" icon={<IconBolt size={14} />}>
                      AUTHENTICATION templates use a fixed shape — Meta auto-generates the body. v1
                      ships COPY_CODE only; ONE_TAP / ZERO_TAP need Android cert hash + zero-tap
                      legal sign-off (deferred).
                    </Alert>
                    <Switch
                      label="Add security recommendation"
                      description="Adds the boilerplate “For your security, do not share this code.” line."
                      checked={addSecurityRecommendation}
                      onChange={(e) => setAddSecurityRecommendation(e.currentTarget.checked)}
                    />
                    <NumberInput
                      label="Code expiration (minutes)"
                      description="1–90 minutes"
                      min={1}
                      max={90}
                      value={codeExpirationMinutes}
                      onChange={(v) => setCodeExpirationMinutes(typeof v === 'number' ? v : '')}
                    />
                    <TextInput
                      label="OTP button label"
                      description="Plain text — no variables, emojis, newlines, or formatting."
                      maxLength={25}
                      value={otpButtonText}
                      onChange={(e) => setOtpButtonText(e.currentTarget.value)}
                    />
                    <NumberInput
                      label="Validity period (seconds)"
                      description="30–900 (30s–15m). Leave blank for Meta default (10 minutes)."
                      min={30}
                      max={900}
                      value={ttlSeconds}
                      onChange={(v) => setTtlSeconds(typeof v === 'number' ? v : '')}
                    />
                  </>
                )}

                {validationError && (
                  <Alert color="red" icon={<IconWand size={14} />}>
                    {validationError}
                  </Alert>
                )}

                <Group justify="end">
                  <Button variant="default" onClick={() => navigate({ to: '/vendor/templates' })}>
                    Cancel
                  </Button>
                  <Button
                    leftSection={<IconRocket size={14} />}
                    disabled={!!validationError}
                    loading={create.isPending}
                    onClick={() => create.mutate(dto)}
                  >
                    Submit for review
                  </Button>
                </Group>
                <Text size="xs" c="dimmed">
                  Meta typically reviews templates within 24 hours.
                </Text>
              </Stack>
            </Card>
          </Grid.Col>

          {/* ── Preview pane ───────────────────────────── */}
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="xs" pos="sticky" top={80}>
              <Text size="sm" fw={500}>
                Live preview
              </Text>
              <TemplatePreview
                template={{
                  category,
                  header:
                    headerFormat === 'NONE' || templateType === 'CAROUSEL'
                      ? undefined
                      : {
                          format: headerFormat,
                          text: headerText,
                          ...(headerMediaPreview ? { mediaPreviewUrl: headerMediaPreview } : {}),
                        },
                  bodyText,
                  bodyExamples,
                  bodyVariableNames,
                  parameterFormat,
                  footerText: templateType === 'STANDARD' ? footerText : '',
                  buttons:
                    templateType === 'CAROUSEL'
                      ? []
                      : buttons.map((b) => ({
                          type: b.type,
                          text: 'text' in b ? b.text : undefined,
                          url: 'url' in b ? b.url : undefined,
                          phone_number: 'phone_number' in b ? b.phone_number : undefined,
                          copyCodeExample: 'copyCodeExample' in b ? b.copyCodeExample : undefined,
                        })),
                  limitedTimeOffer:
                    templateType === 'LIMITED_TIME_OFFER'
                      ? { text: ltoText, hasExpiration: ltoHasExpiration }
                      : undefined,
                  carouselCards:
                    templateType === 'CAROUSEL'
                      ? carouselCards.map((c) => ({
                          headerFormat: cardLayout.headerFormat,
                          mediaPreviewUrl: c.previewUrl,
                          bodyText: cardLayout.hasBody ? c.bodyText : undefined,
                          buttons: c.buttons.map((b) => ({ type: b.type, text: b.text })),
                        }))
                      : undefined,
                  addSecurityRecommendation,
                  codeExpirationMinutes:
                    typeof codeExpirationMinutes === 'number' ? codeExpirationMinutes : null,
                }}
              />
              <Paper p="xs" withBorder>
                <Text size="xs" c="dimmed">
                  Variables resolve from the sample values you enter above. Recipients see their own
                  values at send time.
                </Text>
              </Paper>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </motion.div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────

function canonicaliseName(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 512);
}

function bufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

// ── Buttons builder ───────────────────────────────────────────────────
function ButtonsBuilder({
  buttons,
  allowedTypes,
  onAdd,
  onUpdate,
  onRemove,
}: {
  buttons: TemplateButton[];
  /** When set, only these button types can be added (e.g. LTO: COPY_CODE + URL). */
  allowedTypes?: ReadonlyArray<(typeof BUTTON_TYPES)[number]>;
  onAdd: (type: (typeof BUTTON_TYPES)[number]) => void;
  onUpdate: (i: number, patch: Partial<TemplateButton>) => void;
  onRemove: (i: number) => void;
}) {
  const addable = allowedTypes ?? BUTTON_TYPES;
  return (
    <Stack gap="xs">
      <Group gap={4}>
        {addable.map((t) => (
          <Button
            key={t}
            size="xs"
            variant="light"
            leftSection={<IconCirclePlus size={12} />}
            onClick={() => onAdd(t)}
          >
            {labelForType(t)}
          </Button>
        ))}
      </Group>
      {buttons.map((b, i) => (
        <Card key={i} withBorder p="xs">
          <Group justify="space-between" mb={4}>
            <Badge variant="light">{b.type}</Badge>
            <ActionIcon color="red" variant="subtle" onClick={() => onRemove(i)}>
              <IconTrash size={14} />
            </ActionIcon>
          </Group>
          <Stack gap={6}>
            {'text' in b && b.type !== 'MPM' && (
              <TextInput
                label="Label"
                size="xs"
                maxLength={25}
                value={b.text ?? ''}
                onChange={(e) =>
                  onUpdate(i, { text: e.currentTarget.value } as Partial<TemplateButton>)
                }
              />
            )}
            {b.type === 'URL' && (
              <>
                <TextInput
                  label="URL"
                  size="xs"
                  placeholder="https://example.com/{{1}}"
                  value={b.url}
                  onChange={(e) =>
                    onUpdate(i, { url: e.currentTarget.value } as Partial<TemplateButton>)
                  }
                />
                {b.url.includes('{{1}}') && (
                  <TextInput
                    label="Example URL"
                    size="xs"
                    placeholder="https://example.com/abc123"
                    value={b.urlExample ?? ''}
                    onChange={(e) =>
                      onUpdate(i, { urlExample: e.currentTarget.value } as Partial<TemplateButton>)
                    }
                  />
                )}
              </>
            )}
            {b.type === 'PHONE_NUMBER' && (
              <TextInput
                label="Phone (E.164, +1234567890)"
                size="xs"
                value={b.phone_number}
                onChange={(e) =>
                  onUpdate(i, { phone_number: e.currentTarget.value } as Partial<TemplateButton>)
                }
              />
            )}
            {b.type === 'COPY_CODE' && (
              <TextInput
                label="Example code"
                size="xs"
                maxLength={20}
                value={b.copyCodeExample}
                onChange={(e) =>
                  onUpdate(i, { copyCodeExample: e.currentTarget.value } as Partial<TemplateButton>)
                }
              />
            )}
            {b.type === 'FLOW' && (
              <Group grow>
                <TextInput
                  label="Flow id"
                  size="xs"
                  value={b.flowId}
                  onChange={(e) =>
                    onUpdate(i, { flowId: e.currentTarget.value } as Partial<TemplateButton>)
                  }
                />
                <Select
                  label="Action"
                  size="xs"
                  data={['navigate', 'data_exchange']}
                  value={b.flowAction}
                  onChange={(v) =>
                    onUpdate(i, {
                      flowAction: (v as 'navigate' | 'data_exchange') ?? 'navigate',
                    } as Partial<TemplateButton>)
                  }
                />
              </Group>
            )}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}

function labelForType(t: string): string {
  switch (t) {
    case 'QUICK_REPLY':
      return 'Quick reply';
    case 'PHONE_NUMBER':
      return 'Phone';
    case 'COPY_CODE':
      return 'Copy code';
    case 'VOICE_CALL':
      return 'Voice';
    default:
      return t.toLowerCase();
  }
}

// ── AI panel ──────────────────────────────────────────────────────────
function AiPanel({
  generating,
  translating,
  currentBody,
  currentFooter,
  onGenerate,
  onTranslate,
}: {
  generating: boolean;
  translating: boolean;
  currentBody: string;
  currentFooter: string;
  onGenerate: (prompt: string) => void;
  onTranslate: (targetLanguage: string) => void;
}) {
  const [prompt, setPrompt] = useState('');
  const [target, setTarget] = useState('hi');
  void currentBody;
  void currentFooter;
  return (
    <Card withBorder>
      <Tabs defaultValue="generate">
        <Tabs.List>
          <Tabs.Tab value="generate" leftSection={<IconSparkles size={12} />}>
            Generate
          </Tabs.Tab>
          <Tabs.Tab value="translate" leftSection={<IconLanguage size={12} />}>
            Translate
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="generate" pt="sm">
          <Group align="flex-end">
            <Textarea
              label="Describe the template you want"
              placeholder="A welcome message for a fitness studio offering 20% off the first month"
              minRows={2}
              autosize
              value={prompt}
              onChange={(e) => setPrompt(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button
              loading={generating}
              disabled={prompt.length < 8}
              onClick={() => onGenerate(prompt)}
            >
              Generate
            </Button>
          </Group>
        </Tabs.Panel>
        <Tabs.Panel value="translate" pt="sm">
          <Group align="flex-end">
            <Select
              label="Target language"
              data={TEMPLATE_LANGUAGES.map((l) => ({
                value: l.code,
                label: `${l.code} — ${l.name}`,
              }))}
              searchable
              value={target}
              onChange={(v) => setTarget(v ?? 'hi')}
              style={{ flex: 1 }}
            />
            <Button loading={translating} onClick={() => onTranslate(target)}>
              Translate body + footer
            </Button>
          </Group>
        </Tabs.Panel>
      </Tabs>
    </Card>
  );
}

export const Route = createFileRoute('/vendor/templates/new')({ component: NewTemplate });

// Use Checkbox/IconWand to silence TS unused-imports if Mantine tree-shakes them out
void Checkbox;
